import { useCallback, useState } from 'react';
import { Alert, ArrowLeftIcon, Pane, Spinner, TickCircleIcon } from 'evergreen-ui';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { toaster, sendSlackMessage } from 'cademy-shared-web';
import { Button } from '..';
import './PaymentForm.css';

export const isExpired = (startedAt) => {
    const date = new Date();
    const now = Math.floor(date.getTime() / 1000);
    const timeSinceStarted = now - parseInt(startedAt);
    return timeSinceStarted > 900;
};

export const PaymentForm = ({ onPaymentComplete, goBack, auth, billingInfo, startedAt }) => {
    const [paymentError, setPaymentError] = useState('');
    const stripe = useStripe();
    const elements = useElements();
    const [processingPayment, setProcessingPayment] = useState(false);
    const [loadingStripe, setLoadingStripe] = useState(true);
    const [shake, setShake] = useState(false);

    const shakeIt = () => {
        setShake(true);
        setTimeout(() => setShake(false), 1000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (paymentError) setPaymentError('');

        // Error if payment isn't made within 15 mins of when the order is started
        if (isExpired(startedAt)) {
            shakeIt();
            sendSlackMessage(
                'Booking timed out',
                ':timer_clock:',
                `Someone took more than 15 mins paying for a booking and was told to start over\n*Location:* ${window.location.href} \n*User Agent:* ${navigator.userAgent}\n *Billing info:* \n \`\`\`${JSON.stringify(billingInfo)}\`\`\``
            );
            return toaster.danger(
                'The order has timed out, please start again',
                'You have 15 minutes to complete a purchase once started and unfortunately the time has passed for this booking. Please start again.'
            );
        }

        setProcessingPayment(true);

        if (!stripe || !elements) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        let billingContact;
        if (auth.iam.authenticated) {
            billingContact = {
                name: `${auth.iam.firstName} ${auth.iam.lastName}`,
                email: auth.iam.email,
            };
        } else {
            billingContact = {
                name: `${billingInfo?.first_name} ${billingInfo?.last_name}`,
                email: billingInfo?.email,
            };
        }

        return stripe
            .confirmPayment({
                elements,
                redirect: 'if_required',
                confirmParams: {
                    payment_method_data: {
                        billing_details: {
                            ...billingContact,
                        },
                    },
                },
            })
            .then((result) => {
                if (result.error) {
                    setProcessingPayment(false);
                    shakeIt();
                    setPaymentError(result.error.message);
                    toaster.danger(result.error.message);
                    sendSlackMessage(
                        'Error on payment',
                        ':money_with_wings:',
                        `Error when taking payment on booking\n*Error message:*${result.error.message} \n \n*Location:* ${window.location.href} \n*User Agent:* ${navigator.userAgent} \n *Billing info:* \n \`\`\`${JSON.stringify(billingInfo)}\`\`\``,
                        undefined,
                        result.error
                    );
                } else if (
                    result.paymentIntent.status === 'succeeded' ||
                    result.paymentIntent.status === 'requires_capture'
                ) {
                    setProcessingPayment(false);
                    toaster.success('Payment successful!');
                    setPaymentError('');
                    onPaymentComplete(result);
                }
            })
            .catch((err) => {
                setProcessingPayment(false);
                shakeIt();
                toaster.danger('Something went wrong 😞', 'Please refresh and try again.');
                sendSlackMessage(
                    'Payment form error',
                    ':money_with_wings:',
                    `Error when taking payment on booking \n*Location:* ${window.location.href} \n*User Agent:* ${navigator.userAgent}`,
                    undefined,
                    err
                );
                console.error(err);
            });
    };

    const handleError = useCallback((err) => {
        if (err) {
            setProcessingPayment(false);
            shakeIt();
            setPaymentError(err.message || 'Something went wrong when loading the card element');
            toaster.danger(
                'Something went wrong when loading the card element',
                err.message || 'Please refresh and try again.'
            );
            sendSlackMessage(
                'Stripe Elements load error',
                ':money_with_wings:',
                `Error when loading stripe element \n*Location:* ${window.location.href} \n*User Agent:* ${navigator.userAgent}`,
                undefined,
                err
            );
            console.error(err);
        }
    }, []);

    const renderBackButton = () => {
        if (!goBack) return null;

        return (
            <Button marginRight={10} onClick={goBack}>
                <ArrowLeftIcon marginRight="10px" /> Back
            </Button>
        );
    };

    return (
        <div>
            <form onSubmit={handleSubmit}>
                {/* <CardElement id="card-element" /> */}
                {loadingStripe ? <Spinner marginX="auto" marginTop="50px" /> : null}
                <PaymentElement
                    className="stripe_element"
                    onReady={() => setLoadingStripe(false)}
                    onLoadError={handleError}
                />
                {paymentError ? (
                    <Alert intent="danger" title={paymentError} marginTop={10} maxWidth="400px" />
                ) : null}

                <Pane
                    marginTop={20}
                    display="flex"
                    alignContent="center"
                    justifyContent="space-between"
                    maxWidth="420px"
                >
                    {renderBackButton()}
                    <Button
                        type="submit"
                        margin="0 20px"
                        appearance="primary"
                        disabled={!stripe}
                        isLoading={processingPayment}
                        className={shake ? 'shake' : null}
                    >
                        <TickCircleIcon marginRight="10px" />
                        Pay
                    </Button>

                    {/* <Button appearance='primary' color='red' onClick={() => setStep(4)}>
                        Skip payment
                    </Button> */}
                </Pane>
            </form>
        </div>
    );
};
